import React, { useEffect, useState } from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ContainerConfigType } from 'src/domains/sportsbook/webview/components/scoreboards/liveTracker/ContainerConfig';
import {
    LiveTrackerIFrameContent,
    LiveTrackerIFrameWrapper,
    LiveTrackerToggleWrapper,
    LiveTrackerToggleWrapperUnified,
    LiveTrackerWrapper,
    ScoreboardWrapper,
    SportWidget,
} from 'src/domains/sportsbook/webview/components/scoreboards/liveTracker/LiveTrackerIframe.style';
import { observer } from 'src/utils/mobx-react';
import { LiveTrackerIframeState } from './LiveTrackerIframe.state';
import { UniverseComponent } from 'src/domains/common/universes';
import { useAppStateContext } from 'src/appState/AppState';

interface ReferencePropsType {
    eventId: number;
    providerName: string;
    containerConfig: ContainerConfigType;
    isOpen: boolean;
}

export const LiveTrackerIframe = observer(
    'LiveTrackerIframe',
    ({ eventId, providerName, containerConfig, isOpen }: ReferencePropsType): JSX.Element => {
        const [state] = useState(() => new LiveTrackerIframeState(isOpen));
        // TODO: [Genius] Iris To remove this check and leave only componentVariants.sportsbookVariant when GLOB-37 is fully live
        const {
            componentVariants: { sportsbookVariant },
            unifiedSportEventsTable,
        } = useAppStateContext().unifiedComponentConfig;
        const LiveTrackerToggleWrapperComponent = unifiedSportEventsTable
            ? LiveTrackerToggleWrapperUnified
            : LiveTrackerToggleWrapper;

        const { containerId, scriptSrc } = containerConfig;
        const { isOpen: isTrackerOpen, mountKey } = state;

        useEffect(() => {
            if (isTrackerOpen === false) {
                return;
            }

            let widgetParams: HTMLScriptElement | null = null;
            let widgetScript: HTMLScriptElement | null = null;

            const timeoutId = setTimeout(() => {
                widgetParams = document.createElement('script');
                widgetParams.type = 'text/javascript';
                widgetParams.innerHTML = `
            var widget_params = {
                container_id: '${containerId}',
                id: '${eventId}',
                provider: '${providerName}',
                onError: function(err){console.log('error', err);} 
            };
        `;

                const separator = scriptSrc.includes('?') ? '&' : '?';
                widgetScript = document.createElement('script');
                widgetScript.src = `${scriptSrc}${separator}_=${mountKey}`;

                document.body.appendChild(widgetParams);
                document.body.appendChild(widgetScript);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                widgetParams?.remove();
                widgetScript?.remove();

                const widget = document.getElementById(containerId);
                if (widget !== null) {
                    widget.replaceChildren();
                }
            };
        }, [isTrackerOpen, mountKey, eventId, providerName, containerId, scriptSrc]);

        return (
            <LiveTrackerIFrameWrapper variant={sportsbookVariant}>
                <LiveTrackerIFrameContent
                    isOpen={isTrackerOpen}
                    variant={sportsbookVariant}
                >
                    <LiveTrackerToggleWrapperComponent
                        handleToggle={state.toggle}
                        isOpen={isTrackerOpen}
                        title={
                            <I18n
                                langKey='scoreboard.live-tracker.title'
                                defaultText='Live tracker'
                            />
                        }
                    />

                    {isTrackerOpen ? (
                        <UniverseComponent>
                            {(universeVersion): React.ReactElement => (
                                <LiveTrackerWrapper
                                    key={mountKey}
                                    variant={sportsbookVariant}
                                    bgImage={universeVersion.liveTrackerBgUrl}
                                    isOpen={true}
                                >
                                    <SportWidget id={containerId} />
                                    <ScoreboardWrapper eventId={eventId} />
                                </LiveTrackerWrapper>
                            )}
                        </UniverseComponent>
                    ) : null}
                </LiveTrackerIFrameContent>
            </LiveTrackerIFrameWrapper>
        );
    }
);
